import { useEffect } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { type Dispatch, type UnknownAction } from "redux";
import { v4 } from "uuid";

import {
  addExternalUser as addExternalUserAction,
  addExternalUserReset as addExternalUserResetAction,
  updateExternalUser as updateExternalUserAction,
  updateExternalUserReset as updateExternalUserResetAction,
} from "../../../../actions";
import { WFPButton } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Spinner } from "../../../../components/ui/spinner";
import { getCountryObjectFromCache, isEmpty } from "../../../../utils";
import { ExternalUserForm, EXTERNAL_USER_FORM_ID, type ExternalUserFormValues } from "../Form";
import { isExternalUserType, type ExternalUser, type ExternalUserPayload } from "../../types";

interface OwnProps {
  externalUser: ExternalUser | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface StateProps {
  fieldErrors: { data?: Record<string, unknown> };
  isLoading: boolean;
}

interface DispatchProps {
  addExternalUser: (externalUser: ExternalUserPayload) => Promise<unknown>;
  updateExternalUser: (id: number | string, externalUser: ExternalUserPayload) => Promise<unknown>;
  resetExternalUserErrors: () => void;
}

interface RootState {
  addExternalUser: { error?: Record<string, unknown>; isLoading: boolean };
  updateExternalUser: { error?: { data?: Record<string, unknown> }; isLoading: boolean };
}

type ExternalUserModalProps = OwnProps & StateProps & DispatchProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction) as unknown as Promise<unknown>;

export const adaptFormValues = (
  values: ExternalUserFormValues,
  externalUser: ExternalUser | null
): ExternalUserPayload => ({
  person: {
    // Keep the existing person on edit: the legacy form regenerated the uuid on
    // every save, which rewrote the person's identity.
    ...(externalUser?.person?.id === undefined ? {} : { id: externalUser.person.id }),
    uuid: externalUser?.person?.uuid ?? v4(),
    first_name: values.first_name,
    last_name: values.last_name,
  },
  email: values.email,
  organization: values.organization,
  type: values.type,
  ciam_user_created: true,
  country: getCountryObjectFromCache().id,
});

export const adaptInitialValues = (
  externalUser: ExternalUser | null
): Partial<ExternalUserFormValues> | undefined =>
  externalUser
    ? {
        first_name: externalUser.person?.first_name ?? "",
        last_name: externalUser.person?.last_name ?? "",
        email: externalUser.email ?? "",
        type: isExternalUserType(externalUser.type) ? externalUser.type : undefined,
        organization: externalUser.organization ?? "",
      }
    : undefined;

export const ExternalUserModal = ({
  externalUser,
  isOpen,
  onClose,
  onSaved,
  fieldErrors,
  isLoading,
  addExternalUser,
  updateExternalUser,
  resetExternalUserErrors,
}: ExternalUserModalProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      resetExternalUserErrors();
    }
  }, [isOpen, resetExternalUserErrors]);

  const onValidate = (formValues: ExternalUserFormValues) => {
    const payload = adaptFormValues(formValues, externalUser);
    const promise = externalUser?.id
      ? updateExternalUser(externalUser.id, payload)
      : addExternalUser(payload);

    promise.then(result => {
      if (result) {
        onSaved();
      }
    });
  };

  const preventCloseWhileSaving = (event: Event) => {
    if (isLoading) {
      event.preventDefault();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && !isLoading && onClose()}>
      <DialogContent
        className="sm:max-w-xl"
        aria-describedby={undefined}
        showCloseButton={!isLoading}
        onEscapeKeyDown={preventCloseWhileSaving}
        onPointerDownOutside={preventCloseWhileSaving}
      >
        <DialogHeader>
          <DialogTitle>
            {externalUser
              ? t("externalUserDetails.title")
              : t("externalUsers.newExternalUserFormTitle")}
          </DialogTitle>
        </DialogHeader>
        <ExternalUserForm
          fieldErrors={fieldErrors}
          initialValues={adaptInitialValues(externalUser)}
          onValidate={onValidate}
        />
        <DialogFooter>
          <WFPButton type="button" variant="outline" disabled={isLoading} onClick={onClose}>
            {t("common.cancel")}
          </WFPButton>
          <WFPButton type="submit" form={EXTERNAL_USER_FORM_ID} disabled={isLoading}>
            {isLoading && <Spinner className="size-4" />}
            {t("common.save")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  // resourceAdd dispatches `error.response.data` while resourceUpdate dispatches
  // the whole `error.response`, so the add shape needs wrapping to match.
  let fieldErrors: { data?: Record<string, unknown> } = {};
  if (!isEmpty(state.addExternalUser.error)) {
    fieldErrors = { data: state.addExternalUser.error };
  } else if (!isEmpty(state.updateExternalUser.error)) {
    fieldErrors = state.updateExternalUser.error ?? {};
  }

  return {
    fieldErrors,
    isLoading: state.addExternalUser.isLoading || state.updateExternalUser.isLoading,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  addExternalUser: externalUser => dispatchAction(dispatch, addExternalUserAction(externalUser)),
  updateExternalUser: (id, externalUser) =>
    dispatchAction(dispatch, updateExternalUserAction(id, externalUser)),
  resetExternalUserErrors: () => {
    dispatchAction(dispatch, addExternalUserResetAction());
    dispatchAction(dispatch, updateExternalUserResetAction());
  },
});

export const ConnectedExternalUserModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(ExternalUserModal);
